import { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { validationErrorType } from 'modules/healthPlans/enum';
import {
  createHealthPlan,
  fetchHealthPlans,
  editHealthPlan,
} from 'modules/healthPlans/healthPlansSlice';
import HealthPlansComponent from 'modules/healthPlans/components/HealthPlansComponent/HealthPlansComponent';
import { searchQueryMinLength, status } from 'constants/general';

// ###############################################################
// <------------------------ Constants -------------------------->
// ###############################################################
// ###############################################################
// <---------------------- End Constants ------------------------>
// ###############################################################
const HealthPlansContainer = () => {
  // ###############################################################
  // <------------------------- Globals --------------------------->
  // ###############################################################
  const dispatch = useDispatch();
  const editHealthPlanStatus = useSelector(state => state.healthPlans.editHealthPlan.status);
  const createHealthPlanStatus = useSelector(state => state.healthPlans.createHealthPlan.status);

  // ###############################################################
  // <----------------------- End Globals ------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------ useState --------------------------->
  // ###############################################################
  const [searchQuery, setSearchQuery] = useState('');
  const [healthplansQueryString, setHealthPlansQueryString] = useState({
    pagenumber: 1,
    pagesize: 10,
  });
  const [isCreateHealthPlanModalHidden, setIsCreateHealthPlanModalHidden] = useState(true);
  const [formFieldsValues, setFormFieldsValues] = useState({
    healthPlan: '',
    insurance: '',
    key: '',
    country: '',
  });
  const [formFieldsValidation, setFormFieldsValidation] = useState({
    healthPlan: { status: validationErrorType.valid, errMessage: '' },
    insurance: { status: validationErrorType.valid, errMessage: '' },
  });

  // ###############################################################
  // <---------------------- End useState ------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------ useEffect -------------------------->
  // ###############################################################
  useEffect(() => {
    dispatch(fetchHealthPlans(healthplansQueryString));
  }, [dispatch, healthplansQueryString]);

  const handleCancelHealthPlan = () => {
    setFormFieldsValues({
      healthPlan: '',
      insurance: '',
      key: '',
    });
    setIsCreateHealthPlanModalHidden(true);
  };

  useEffect(() => {
    if (editHealthPlanStatus === status.SUCCESS || createHealthPlanStatus === status.SUCCESS) {
      setHealthPlansQueryString({
        pagenumber: 1,
        pagesize: 10,
      });
      handleCancelHealthPlan();
    }
  }, [editHealthPlanStatus, createHealthPlanStatus]);

  // ###############################################################
  // <---------------------- End useEffect ------------------------>
  // ###############################################################
  // ###############################################################
  // <--------------------- rest of hooks ------------------------->
  // ###############################################################
  const searchBarInputTimeout = useRef(null);
  // ###############################################################
  // <-------------------- End rest of hooks ---------------------->
  // ###############################################################
  // ###############################################################
  // <-------------------------- Logic ---------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------ End Logic -------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------- Actions --------------------------->
  // ###############################################################
  // ###############################################################
  // <----------------------- End Actions ------------------------->
  // ###############################################################
  // ###############################################################
  // <----------------------- UI Handlers ------------------------->
  // ###############################################################

  const handleInputFieldChange = (fieldName, fieldEvent) => {
    let value = null;
    if (['insurance', 'country'].includes(fieldName)) {
      value = fieldEvent.key;
    } else {
      value = fieldEvent.target.value;
    }
    setFormFieldsValues(prevValue => {
      const fieldsObj = { ...prevValue };
      fieldsObj[fieldName] = value;
      return fieldsObj;
    });
  };

  const handleSearchInputChange = (searchTerm, reset) => {
    const queryString = {
      ...healthplansQueryString,
      pagenumber: 1,
      isShowMore: false,
    };
    if (searchTerm) queryString.searchTerm = searchTerm;
    if (reset) delete queryString.searchTerm;

    setHealthPlansQueryString(queryString);
  };

  const handleSubmitHealthPlan = () => {
    if (formFieldsValues.key) {
      const payload = {
        id: formFieldsValues.key,
        data: {
          Name: formFieldsValues.healthPlan,
          Active: formFieldsValues.active,
        },
      };
      dispatch(editHealthPlan(payload));
    } else {
      const payload = {
        data: {
          Name: formFieldsValues.healthPlan,
          InsuranceCompanyId: formFieldsValues.insurance || 1,
          countryId: formFieldsValues.country,
        },
      };
      dispatch(createHealthPlan(payload));
    }
    /* setFormFieldsValues({
      healthPlan: '',
      insurance: '',
      key: '',
    }); */
  };

  const handleSearchBarInputChange = event => {
    const { value } = event.target;

    if (value.length > 30) return;
    setSearchQuery(value);

    if (searchBarInputTimeout.current) clearTimeout(searchBarInputTimeout.current);

    if (value && value.length >= searchQueryMinLength) {
      searchBarInputTimeout.current = setTimeout(() => {
        clearTimeout(searchBarInputTimeout.current);

        handleSearchInputChange(value);
      }, 500);
    } else if (value.length < searchQueryMinLength && healthplansQueryString.searchTerm)
      handleSearchInputChange(value, true);
  };

  const handleShowMoreClick = () => {
    const data = {
      ...healthplansQueryString,
    };
    data.pagenumber += 1;
    data.isShowMore = true;
    setHealthPlansQueryString(data);
  };

  const handleHealthPlanEdit = healthPlansCategories => {
    setFormFieldsValues({
      healthPlan: healthPlansCategories.name,
      insurance: healthPlansCategories.insuranceCompany.key,
      country: healthPlansCategories.countryId,
      key: healthPlansCategories.id,
      status: healthPlansCategories.active,
    });
    setIsCreateHealthPlanModalHidden(false);
  };

  const handleHealthPlanDeactivate = rowDataPayload => {
    const { id, name, active } = rowDataPayload;
    const payload = {
      id,
      data: {
        Name: name,
        Active: !active,
      },
    };
    dispatch(editHealthPlan(payload));
  };

  // ###############################################################
  // <--------------------- End UI Handlers ----------------------->
  // ###############################################################
  // ###############################################################
  // <-------------------------- Render --------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------ End Render ------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------- Return UI ------------------------->
  // ###############################################################
  return (
    <HealthPlansComponent
      isCreateHealthPlanModalHidden={isCreateHealthPlanModalHidden}
      setIsCreateHealthPlanModalHidden={setIsCreateHealthPlanModalHidden}
      formFieldsValues={formFieldsValues}
      setFormFieldsValues={setFormFieldsValues}
      formFieldsValidation={formFieldsValidation}
      setFormFieldsValidation={setFormFieldsValidation}
      handleSubmitHealthPlan={handleSubmitHealthPlan}
      handleInputFieldChange={handleInputFieldChange}
      searchQuery={searchQuery}
      handleSearchBarInputChange={handleSearchBarInputChange}
      healthplansQueryString={healthplansQueryString}
      handleShowMoreClick={handleShowMoreClick}
      handleHealthPlanEdit={handleHealthPlanEdit}
      handleHealthPlanDeactivate={handleHealthPlanDeactivate}
      handleCancelHealthPlan={handleCancelHealthPlan}
    />
  );
  // ###############################################################
  // <----------------------- End Return UI ----------------------->
  // ###############################################################
};

HealthPlansContainer.propTypes = {};

HealthPlansContainer.defaultProps = {};

export default HealthPlansContainer;
